import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db } from '../datos/db.js'
import { recursosVisibles } from '../datos/consultas.js'
import { TituloPagina } from '../comunes/AppShell.js'
import { DIAS, aReloj, franjasDescubiertas } from './elegibles.js'
import { PlantillaSemanal } from './PlantillaSemanal.js'
import { DiaConsultorios } from './DiaConsultorios.js'

/**
 * Contenedor de las dos pantallas de planificación semanal:
 *
 *   - **Plantilla semanal**: edición por consultorio.
 *   - **Día**: vista de un día con todos los consultorios como columnas.
 */

type Solapa = 'plantilla' | 'dia'

export function Consultorios() {
  const [solapa, setSolapa] = useState<Solapa>('plantilla')

  const recursos = useLiveQuery(() => recursosVisibles(), [])
  const tipos = useLiveQuery(() => db.recurso_tipo.toArray(), [])
  // Los horarios y afinidades borrados (tombstones) no habilitan a nadie: se
  // filtran acá, una sola vez, y los hijos reciben sólo los vigentes.
  const horarios = useLiveQuery(() => db.recurso_horario.filter((h) => !h.deleted_at).toArray(), [])
  const afinidades = useLiveQuery(() => db.recurso_afinidad.filter((a) => !a.deleted_at).toArray(), [])
  const asignaciones = useLiveQuery(() => db.asignacion_espacio.toArray(), [])

  const esPersona = useMemo(() => {
    if (!tipos) return new Set<string>()
    return new Set(tipos.filter((t) => t.es_persona).map((t) => t.id))
  }, [tipos])

  const esEspacio = useMemo(() => {
    if (!tipos) return new Set<string>()
    return new Set(tipos.filter((t) => t.es_espacio).map((t) => t.id))
  }, [tipos])

  const personas = useMemo(
    () => (recursos ?? []).filter((r) => esPersona.has(r.recurso_tipo_id)),
    [recursos, esPersona],
  )

  const espacios = useMemo(
    () => (recursos ?? []).filter((r) => esEspacio.has(r.recurso_tipo_id)),
    [recursos, esEspacio],
  )

  const descubiertas = useMemo(() => {
    if (!horarios || !asignaciones) return []
    return franjasDescubiertas(personas, horarios, asignaciones)
  }, [personas, horarios, asignaciones])

  if (!recursos || !tipos || !horarios || !afinidades || !asignaciones) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-slate-500">
        Cargando consultorios…
      </div>
    )
  }

  return (
    <>
      <TituloPagina titulo="Consultorios" />

      {/* Franjas descubiertas: persona con horario que no cubre ningún consultorio. */}
      {descubiertas.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm font-medium text-amber-900">
            Franjas horarias sin consultorio asignado
          </p>
          <p className="mt-0.5 text-xs text-amber-800">
            Profesionales con horario cargado cuya franja no cubre ningún consultorio.
          </p>
          <ul className="mt-2 space-y-1">
            {descubiertas.map((fd, i) => (
              <li key={`${fd.personaId}-${fd.diaSemana}-${fd.desde}-${i}`} className="flex items-baseline gap-2 text-sm text-amber-900">
                <span className="font-medium">{fd.nombre}</span>
                <span className="text-amber-700">
                  {DIAS[fd.diaSemana]} {aReloj(fd.desde)}–{aReloj(fd.hasta)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Solapas */}
      <div className="mb-4 flex gap-1 rounded-lg bg-slate-100 p-1">
        {(
          [
            ['plantilla', 'Plantilla semanal'],
            ['dia', 'Día'],
          ] as const
        ).map(([id, etiqueta]) => (
          <button
            key={id}
            type="button"
            onClick={() => setSolapa(id)}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition ${
              solapa === id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {etiqueta}
          </button>
        ))}
      </div>

      {solapa === 'plantilla' ? (
        <PlantillaSemanal
          espacios={espacios}
          personas={personas}
          horarios={horarios}
          afinidades={afinidades}
          asignaciones={asignaciones}
          sucursalId={espacios[0]?.sucursal_id ?? personas[0]?.sucursal_id ?? null}
        />
      ) : (
        <DiaConsultorios
          espacios={espacios}
          personas={personas}
          horarios={horarios}
          asignaciones={asignaciones}
        />
      )}
    </>
  )
}